import './lib/sentry';
import '@micdrop/web';
import '@micdrop/web/silero';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

import CVDownloadPage from '@/features/cv/pages/CVDownloadPage';
import CVEditPage from '@/features/cv/pages/CVEditPage.tsx';
import { CVPayloadPreviewPage } from '@/features/cv/pages/CVPayloadPreviewPage';
import { CVPreviewPage } from '@/features/cv/pages/CVPreviewPage';
import CVTranslationTestPage from '@/features/cv/pages/CVTranslationTestPage';

import { AppToaster } from './features/common/components/AppToaster';
import { HomeGuard } from './features/common/components/HomeGuard';
import { MaintenanceGuard } from './features/common/components/MaintenanceGuard';
import { InterviewProvider } from './features/common/contexts/interview.context';
import { DashboardRedirectPage } from './features/common/pages/DashboardRedirectPage';
import LegalsPage from './features/common/pages/LegalsPage';
import { registerAllTemplates } from './features/cv/templates/register';
import './index.css';
import './lib/i18n';
import { FeedbackFab, FeedbackProvider, PhoneBanner } from './features/feedback';
import HomePage from './features/home/pages/HomePage.tsx';
import InterviewPage from './features/kiosk/pages/flow/InterviewPage';
import LanguagePage from './features/kiosk/pages/flow/LanguagePage';
import OnboardingPage from './features/kiosk/pages/flow/OnboardingPage';
import OtpPage from './features/kiosk/pages/flow/OtpPage';
import PaymentPage from './features/kiosk/pages/flow/PaymentPage';
import ProceedToPaymentPage from './features/kiosk/pages/flow/ProceedToPaymentPage';
import RegisterCguPage from './features/kiosk/pages/flow/RegisterCguPage';
import RegisterCityPage from './features/kiosk/pages/flow/RegisterCityPage';
import RegisterLayout from './features/kiosk/pages/flow/RegisterLayout';
import RegisterNamesPage from './features/kiosk/pages/flow/RegisterNamesPage';
import RegisterPhonePage from './features/kiosk/pages/flow/RegisterPhonePage';
import KioskFlowLayout from './features/kiosk/pages/KioskFlowLayout';
import { useBooth } from './features/settings/hooks/useBooth';

registerAllTemplates();

const container = document.getElementById('root')!;
const root = createRoot(container, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo?.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

import { Agentation } from 'agentation';

function AppFooterOverlays() {
  const { pathname } = useLocation();
  const { isMaintenanceModeEnabled } = useBooth();
  if (isMaintenanceModeEnabled || pathname.startsWith('/cv')) return null;
  return (
    <>
      <PhoneBanner />
      <FeedbackFab />
      {import.meta.env.DEV && <Agentation endpoint="http://localhost:4747" />}
    </>
  );
}

root.render(
  // <StrictMode>
  <BrowserRouter>
    <InterviewProvider>
      <FeedbackProvider>
        <MaintenanceGuard>
          <Routes>
            <Route
              path="/"
              element={
                <HomeGuard>
                  <HomePage />
                </HomeGuard>
              }
            />
            <Route element={<KioskFlowLayout />}>
              <Route path="/language" element={<LanguagePage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/register" element={<RegisterLayout />}>
                <Route index element={<RegisterCguPage />} />
                <Route path="cgu" element={<RegisterCguPage />} />
                <Route path="names" element={<RegisterNamesPage />} />
                <Route path="city" element={<RegisterCityPage />} />
                <Route path="phone" element={<RegisterPhonePage />} />
              </Route>
              <Route path="/otp" element={<OtpPage />} />
              <Route path="/payment">
                <Route index element={<PaymentPage />} />
                <Route path="proceed" element={<ProceedToPaymentPage />} />
              </Route>
              <Route path="/interview" element={<InterviewPage />} />
            </Route>
            <Route path="/dashboard/*" element={<DashboardRedirectPage />} />
            <Route path="/legals" element={<LegalsPage />} />
            <Route
              path="/cv/preview/payload"
              element={(() => {
                console.log('[main.tsx] Rendering CVPayloadPreviewPage for /cv/preview/payload');
                return <CVPayloadPreviewPage />;
              })()}
            />
            <Route path="/cv/test-translation" element={<CVTranslationTestPage />} />
            <Route path="/cv/:id">
              <Route index element={<CVDownloadPage />} />
              <Route path="edit" element={<CVEditPage />} />
              <Route path="preview" element={<CVPreviewPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </MaintenanceGuard>
        <AppToaster />
        <AppFooterOverlays />
      </FeedbackProvider>
    </InterviewProvider>
  </BrowserRouter>,
  // </StrictMode>,
);
